import { withEmotionCache } from '@emotion/react';
import { unstable_useEnhancedEffect as useEnhancedEffect } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import type { SnackbarOrigin } from '@mui/material/Snackbar';
import Snackbar from '@mui/material/Snackbar';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { LinksFunction, LoaderArgs, MetaFunction, SerializeFrom } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useFetcher,
  useLoaderData,
} from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import { withSentry } from '@sentry/remix';
import Debug from 'debug';
import type { LDFlagSet, LDSingleKindContext } from 'launchdarkly-js-client-sdk';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useChangeLanguage } from 'remix-i18next';
import { useGlobalPendingState } from 'remix-utils';
import { useInitShippingInfo } from '~/components/hooks/useInitShippingInfo';
import { useInitStorageInfo } from '~/components/hooks/useInitStorageInfo';
import { useMetadata } from '~/components/hooks/useMetadata';
import { getLdFlag } from '~/components/launchdarkly.server';
import { MuiRemixLink } from '~/components/shared/links';
import type { TDesignLab } from '~/routes/design-lab';
import { cartApi, scApi } from '~/services/api.server';
import { getUpdatedAuthentication } from '~/services/auth.server';
import { i18nextCookie, type Notification } from '~/services/cookies.server';
import { i18nServer } from '~/services/i18n.server';
import { commitSession, getSession } from '~/services/session.server';
import globalStylesheetUrl from '~/styles/global.css';
import tailwindStylesheetUrl from '~/styles/tailwind.css';
import { AuthContext, useFlags, useLDContext } from '~/utils/contexts';
import { ClientStyleContext } from '~/utils/emotion/StylesContext';
import * as gtm from '~/utils/gtm.client';
import { getLocale } from '~/utils/i18n';
import theme from '~/utils/mui/theme';
import logger from './utils/logger';

declare global {
  interface Window {
    ENV: {
      APP_NAME: string;
      APP_VERSION: string;
      AWS_DEFAULT_REGION: string;
      AWS_IDENTITY_POOL_ID: string;
      AWS_UPLOAD_RESOURCES_BUCKET: string;
      LD_CLIENT_SIDE_ID: string;
      LOGROCKET_ID: string | undefined;
      NODE_ENV: 'development' | 'production' | 'test';
      SENTRY_DSN: string | undefined;
      SNITCHER_API_KEY: string | undefined;
    };
    theme: typeof theme;
    __ld: {
      ssrFlags: LDFlagSet;
      ldContext: LDSingleKindContext;
    };
    dataLayer: Record<string, any>[];
  }
}

const debug = Debug('sc:mango:root');

export const handle = {
  // in the handle export, we could add a i18n key with namespaces our route will need to load
  // this key can be a single string or an array of strings
  i18n: 'root',
};

export const links: LinksFunction = () => {
  return [
    { rel: 'stylesheet', href: globalStylesheetUrl },
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
    {
      rel: 'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500;1,700&display=swap',
      crossOrigin: 'anonymous',
    },
    { rel: 'stylesheet', href: tailwindStylesheetUrl },
  ];
};

export const meta: MetaFunction<typeof loader> = ({ data }) => ({
  charset: 'utf-8',
  title: 'SwagCharm',
  description: data.description,
  viewport: 'width=device-width,initial-scale=1',
  'theme-color': theme.palette.primary.main,
  keywords: 'swag,pack,packs,products',
});

export type LoaderData = SerializeFrom<typeof loader>;

export async function loader({ request }: LoaderArgs) {
  const [{ user, headers = new Headers() }, locale, t, session] = await Promise.all([
    getUpdatedAuthentication(request),
    i18nServer.getLocale(request),
    i18nServer.getFixedT(request, 'root'),
    getSession(request),
  ]);

  if (!session.id) {
    headers.append('Set-Cookie', await commitSession(session));
    throw redirect(request.url, { headers });
  }

  if (process.env.NODE_ENV === 'development') {
    // don't delete (example of how to use feature flags in loaders)
    const testFlag = await getLdFlag(user, 'test');
    debug('testFlag:', testFlag);
  }

  const description = t('description');
  debug('description:', description);

  const gtm_id = process.env.GTM_ID;

  const cart = cartApi.getCart(session);
  const designLabQueue = session.get('designLabQueue') as TDesignLab[];
  const designLabQueueQty = designLabQueue?.length || 0;

  const notification = session.get('notification') as Notification;
  const detectedCompany = session.get('detectedCompany');

  const new_sign_up = session.get('new_sign_up');
  new_sign_up && session.unset('new_sign_up');

  const new_login = session.get('new_login');
  new_login && session.unset('new_login');

  const userFirstLanding = session.get('userFirstLanding');
  const keepModalOpen = session.get('keepModalOpen');

  if (!session.has('userFirstLanding') && !userFirstLanding) {
    session.set('userFirstLanding', true);
  }

  const accountId = session.get('accountId');
  if ((new_login || new_sign_up) && user?.accessToken) {
    const response = await scApi.addArtworksToAccount(user.accessToken, accountId, session.id);
    debug('artworks added to account:', response);
  }

  headers.append('Set-Cookie', await i18nextCookie.serialize(locale));
  headers.append('Set-Cookie', await commitSession(session));

  const flagsKeys = ['enable-web-stores'];
  const flagsValues = (await Promise.all(flagsKeys.map((key) => getLdFlag(user, key)))) as boolean[];
  const flags = Object.fromEntries(flagsKeys.map((_, i) => [flagsKeys[i], flagsValues[i]]));
  logger.info('Launchdarkly flags', flags);

  return json(
    {
      flags,
      keepModalOpen,
      userFirstLanding,
      detectedCompany,
      profile: user?.profile,
      locale: getLocale(locale),
      description,
      cart,
      designLabQueueQty,
      gtm_id,
      notification,
      ENV: {
        APP_NAME: process.env.npm_package_name,
        APP_VERSION: process.env.npm_package_version,
        AWS_DEFAULT_REGION: process.env.AWS_DEFAULT_REGION,
        AWS_IDENTITY_POOL_ID: process.env.AWS_IDENTITY_POOL_ID,
        AWS_UPLOAD_RESOURCES_BUCKET: process.env.AWS_UPLOAD_RESOURCES_BUCKET,
        LD_CLIENT_SIDE_ID: process.env.LD_CLIENT_SIDE_ID,
        LOGROCKET_ID: process.env.LOGROCKET_ID,
        NODE_ENV: process.env.NODE_ENV,
        SENTRY_DSN: process.env.SENTRY_DSN,
        SNITCHER_API_KEY: process.env.SNITCHER_API_KEY,
      },
      new_sign_up,
      new_login,
      announceKitWidgetId: process.env.ANNOUNCEKIT_WIDGET_ID,
    },
    { headers }
  );
}

type DocumentProps = {
  children: React.ReactNode;
};

const Document = withEmotionCache(({ children }: DocumentProps, emotionCache) => {
  const clientStyleData = React.useContext(ClientStyleContext);

  // only executed on client
  useEnhancedEffect(() => {
    // re-link sheet container
    emotionCache.sheet.container = document.head;
    // re-inject tags
    const tags = emotionCache.sheet.tags;
    emotionCache.sheet.flush();
    tags.forEach((tag) => {
      // eslint-disable-next-line no-underscore-dangle
      (emotionCache.sheet as any)._insertTag(tag);
    });
    // reset cache to reapply global styles
    clientStyleData.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{children}</>;
});

function LaunchDarklyScript() {
  const ssrFlags = useFlags();
  const ldContext = useLDContext();
  const __ld = JSON.stringify({ ssrFlags, ldContext }, null, 2);

  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `window.__ld=${__ld}`,
      }}
    />
  );
}

function snitcherCallback(identification: { type: string; company: string }) {
  // We want to ignore 'isp' type identifications
  if (!localStorage.getItem('company') && identification && identification.type !== 'isp') {
    // The company was successfully identified!

    // Var assignment to prevent different function serialization between server and client
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let s = localStorage.setItem('company', JSON.stringify(identification.company));
    const event = new CustomEvent('storage');
    window.dispatchEvent(event);
  }
}

function SnitcherScript({ token }: { token: string | undefined }) {
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `window.SpotterSettings={token: '${token}', callback: ${snitcherCallback}}`,
      }}
    />
  );
}

export default withSentry(App);

function App() {
  const { profile, locale, gtm_id, ENV, new_sign_up, new_login, detectedCompany } = useLoaderData<typeof loader>();
  const { i18n } = useTranslation('root');
  const showAutoMockups = useFlags()['show-auto-mockups-temp-05042023'];
  const fetcher = useFetcher();
  const submit = fetcher.submit;

  const metadata = useMetadata();
  useInitShippingInfo(metadata);
  useInitStorageInfo(metadata);

  useChangeLanguage(locale);

  const storageEventOnce = React.useRef(false);
  React.useEffect(() => {
    if (typeof window !== 'undefined' && !storageEventOnce.current) {
      window.addEventListener('storage', () => {
        const company = JSON.parse(localStorage.getItem('company') || '{}');
        const formData = new FormData();
        formData.append('company', JSON.stringify(company));
        formData.append('_action', 'company-detected');
        submit(formData, { method: 'post', action: '/api/artworks' });
        localStorage.removeItem('company');
      });
      storageEventOnce.current = true;
    }
  }, [submit]);

  React.useEffect(() => {
    if (profile?.id && ENV.LOGROCKET_ID && window.location.hostname !== 'localhost') {
      const LogRocket = require('logrocket');
      LogRocket.identify(profile.id, {
        name: profile.displayName,
        email: profile.emails?.[0].value,
      });
    }
  }, [ENV.LOGROCKET_ID, profile?.displayName, profile?.emails, profile?.id]);

  React.useEffect(() => {
    new_sign_up && gtm.push_sign_up();
  }, [new_sign_up]);

  React.useEffect(() => {
    new_login && gtm.push_login();
  }, [new_login]);

  React.useEffect(() => {
    if (ENV.SENTRY_DSN) {
      Sentry.setUser(
        profile?.id
          ? {
              id: profile.id,
              username: profile.displayName,
              email: profile.emails?.[0].value,
            }
          : null
      );
    }
  }, [ENV.SENTRY_DSN, profile?.displayName, profile?.emails, profile?.id]);

  return (
    <html lang={locale} dir={i18n.dir()}>
      <head>
        <Meta />
        <Links />
        <meta name="emotion-insertion-point" content="emotion-insertion-point" />
        <LaunchDarklyScript />
        <script>window.dataLayer = window.dataLayer || [];</script>
        {ENV.SNITCHER_API_KEY && !profile && !detectedCompany && showAutoMockups && (
          <SnitcherScript token={ENV.SNITCHER_API_KEY} />
        )}
        {gtm_id && <script src="/gtm.js" />}
      </head>
      <body id="root">
        {gtm_id && (
          <noscript
            id="gtag-init"
            dangerouslySetInnerHTML={{
              __html: `
                <iframe src="https://www.googletagmanager.com/ns.html?id=${gtm_id}"
                height="0" width="0" style="display:none;visibility:hidden"></iframe>
              `,
            }}
          />
        )}
        <Document>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <AuthContext.Provider value={profile}>
              <GlobalLoader />
              <GlobalNotification />
              <Outlet />
            </AuthContext.Provider>
          </LocalizationProvider>
        </Document>
        <ScrollRestoration />
        <script dangerouslySetInnerHTML={{ __html: `window.ENV = ${JSON.stringify(ENV)}` }} />
        <Scripts />
        <LiveReload />
      </body>
    </html>
  );
}

function GlobalLoader() {
  const active = useGlobalPendingState() !== 'idle';

  const ref = React.useRef<HTMLDivElement>(null);
  const [animationComplete, setAnimationComplete] = React.useState(true);

  React.useEffect(() => {
    if (!ref.current) return;
    if (active) setAnimationComplete(false);

    Promise.allSettled(
      ref.current
        .getAnimations()
        .filter((animation) => (animation instanceof CSSAnimation ? animation.animationName !== 'gradient' : true))
        .map(({ finished }) => finished)
    ).then(() => !active && setAnimationComplete(true));
  }, [active]);

  const [progress, setProgress] = React.useState(20);
  React.useEffect(() => {
    if (animationComplete) {
      setProgress(20);
      return;
    }

    const timeout = Math.max(Math.random() * 1000, 500);
    const timer = setTimeout(() => {
      const diff = Math.random() * 10;
      setProgress(Math.min(progress + diff, 90));
    }, timeout);

    return () => {
      clearTimeout(timer);
    };
  }, [animationComplete, progress]);

  return (
    <Box
      role="progressbar"
      aria-hidden={!active}
      aria-valuetext={active ? 'Loading' : undefined}
      sx={{
        position: 'fixed',
        right: 0,
        left: 0,
        height: 8,
        zIndex: ({ zIndex }) => zIndex.modal + 3,
      }}
    >
      <Box
        ref={ref}
        sx={[
          {
            height: '100%',
            background: ({ palette: { secondary } }) =>
              `linear-gradient(to right, ${secondary.main}, #FFF, ${secondary.main}, #FFF)`,
            backgroundSize: '300% 100%',
            transition: 'all 500ms ease-in-out',
            borderTopRightRadius: 1,
            borderBottomRightRadius: 1,
            animation: 'gradient 2s infinite linear',
          },
          !active && animationComplete && { width: 0, opacity: 0, transition: 'none' },
          active && { width: `${progress}%` },
          !active && !animationComplete && { width: '100%' },
        ]}
      />
    </Box>
  );
}

function GlobalNotification() {
  const { notification } = useLoaderData<typeof loader>();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    setOpen(Boolean(notification));
  }, [notification]);

  function handleClose(_: unknown, reason?: string) {
    if (reason === 'clickaway') return;
    setOpen(false);
  }

  const position = notification?.position || 'top-right';
  const anchorOrigin = {
    vertical: position.split('-')[0] as SnackbarOrigin['vertical'],
    horizontal: position.split('-')[1] as SnackbarOrigin['horizontal'],
  };

  return (
    <Snackbar open={open} autoHideDuration={6000} onClose={handleClose} anchorOrigin={anchorOrigin}>
      <Alert
        onClose={handleClose}
        severity={notification?.severity}
        sx={{
          width: '100%',
          '& > .MuiAlert-message': {
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'baseline',
            gap: { md: '16px' },
            fontWeight: 500,
          },
        }}
      >
        <>
          {notification?.message}
          {notification?.action && (
            <MuiRemixLink
              href={notification.action.href}
              color="inherit"
              sx={{
                color: 'primary.main',
                fontSize: 14,
                display: 'block',
                '&:hover': { color: 'primary.main' },
                fontWeight: 500,
              }}
            >
              {notification.action.label}
            </MuiRemixLink>
          )}
        </>
      </Alert>
    </Snackbar>
  );
}
